import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toastr from "toastr";
import { Dialog } from "@headlessui/react";
import {
    ArrowLeft,
    CheckCircle,
    CircleX,
    Crown,
    Lock,
    MapPin,
    Receipt,
    ShieldCheck,
    Sparkles,
    Store as StoreIcon,
    Ticket,
    User,
    Wallet,
} from "lucide-react";

import { fetchWalletBalance } from "../../../store/walletSlice";
import { usePaymentGateway } from "../../../hooks/usePaymentGateway";
import useGoerOneAuth from "../../../hooks/useGoerOneAuth";
import WalletUtils from "../../../utils/walletUtils";
import { encryptAES } from "../../../utils/crypto";
import { formatINR } from "../../../components/web/VoucherCheckoutPanel";
import {
    buildOrderPayload,
    buildOrderSummary,
    clearCheckoutOrder,
    getCoinLimit,
    isValidOrder,
    loadCheckoutOrder,
} from "../../../utils/checkoutOrder";
import { getApiKey, isApiSuccess, redeemOrder,purchaseVoucherOrder } from "../../../services/api";
import StoreVoucherPicker from "../StoreVoucherPicker";
import { clearApplied } from "../../../store/voucherSlices/storeVoucherSlice";

const upi_logo = "/payment-icon/upi_logo.webp";
const paytm = "/payment-icon/paytm.avif";
const phonePe = "/payment-icon/phonePe.png";
const googlePay = "/payment-icon/googlePay.png";
const upi_paymode = "/payment-icon/upi_paymode.png";

const visa = "/payment-icon/visa.png";
const mastercard = "/payment-icon/mastercard.png";
const rupay = "/payment-icon/rupay.png";
const diners = "/payment-icon/diners.png";
const american = "/payment-icon/american.png";

const sbibank = "/payment-icon/sbibank.png";
const hdfcbank = "/payment-icon/hdfcbank.png";
const icicibank = "/payment-icon/icicibank.png";
const axisbank = "/payment-icon/axisbank.png";
const kotakbank = "/payment-icon/kotakbank.png";
const walletIcon = "/payment-icon/wallet-icon.png";

/* ────────────────────────────────────────────────────────────────────────
 * Wiring: everything backend-specific lives in this block.
 * ──────────────────────────────────────────────────────────────────────── */

const LOGIN_PATH = "/login"; // where a logged-out visitor is sent
const PURCHASE_META = {
    planType: 10, // TODO: the planType your payment-success route expects for store purchases
    channel: "GoerOne",
};

const VOUCHER_META = { planType: 12, udf2: 2, statusCode: "OTS0000" }; // voucher purchases
/**
 * Wallet-only / fully-covered orders (no gateway) are placed here via POST /loyalty/redeem.
 * `payload` is the same object that the online flow sends encrypted (see buildRedeemPayload).
 * Returns { ok: true } on success, or { ok: false, message } / throws on failure.
 */
async function submitOrderDirect(payload) {
    const res = await redeemOrder(payload);
    return { ok: isApiSuccess(res), message: res?.message };
}

/* ────────────────────────────────────────────────────────────────────────
 * Static data
 * ──────────────────────────────────────────────────────────────────────── */

// pg = value the gateway expects in show_payment_mode
const ONLINE_METHODS = [
    {
        id: "upi",
        label: "UPI",
        pg: "UPI",
        icon: upi_paymode,
        recommended: true,
        logos: [[upi_logo, "UPI", "h-5"], [googlePay, "Google Pay", "h-4"], [phonePe, "PhonePe", "h-4"], [paytm, "Paytm", "h-4"]],
    },
    {
        id: "credit",
        label: "Credit Cards",
        pg: "CC",
        emoji: "💳",
        logos: [[visa, "Visa", "h-4"], [mastercard, "MasterCard", "h-4"], [rupay, "RuPay", "h-4"], [diners, "Diners Club", "h-5"], [american, "American Express", "h-5"]],
    },
    {
        id: "debit",
        label: "Debit Cards",
        pg: "DC",
        emoji: "💳",
        logos: [[visa, "Visa", "h-4"], [mastercard, "MasterCard", "h-4"], [rupay, "RuPay", "h-4"]],
    },
    {
        id: "netbanking",
        label: "Net Banking",
        pg: "NB",
        emoji: "🏦",
        logos: [[sbibank, "SBI", "h-5"], [hdfcbank, "HDFC", "h-5"], [icicibank, "ICICI", "h-5"], [axisbank, "Axis", "h-5"], [kotakbank, "Kotak", "h-5"]],
    },
    { id: "emi", label: "EMI", pg: "EMI", emoji: "🧾", sub: "Easy installments" },
];

const round2 = (n) => Math.round(n * 100) / 100;

// 10-character alphanumeric reference (A-Z, 0-9), e.g. "7K2QX9M4TB"
const generateReferenceNumber = (length = 10) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const bytes = new Uint32Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join("");
};

/* ────────────────────────────────────────────────────────────────────────
 * Small pieces
 * ──────────────────────────────────────────────────────────────────────── */

function Card({ title, action, children, className = "" }) {
    return (
        <section className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
            {title && (
                <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
                    <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                    {action}
                </header>
            )}
            {children}
        </section>
    );
}

function SummaryRow({ label, sub, value, text, negative, muted }) {
    return (
        <li className="flex items-start justify-between gap-3 py-2">
            <div className="min-w-0">
                <p className={`text-sm ${muted ? "text-slate-500" : "text-slate-700"}`}>{label}</p>
                {sub && <p className="truncate text-xs text-slate-400">{sub}</p>}
            </div>
            <span className={`shrink-0 text-sm font-medium ${negative || text === "Active" || text === "Free" ? "text-emerald-600" : "text-slate-900"}`}>
                {text ?? `${negative ? "− " : ""}${formatINR(Math.abs(value || 0))}`}
            </span>
        </li>
    );
}

/** Compact membership card: what plan, and what it costs on this order */
function MembershipCard({ membership, credit = 0, creditDays = 0 }) {
    const status = membership.owned ? "Active" : membership.fee > 0 ? formatINR(membership.fee) : "Free";
    const note = membership.owned
        ? "You're already a member. Offers of this plan apply."
        : membership.fee > 0
        ? credit > 0
            ? `12 months. ${formatINR(membership.total)} with GST, after ${formatINR(credit)} credit for ${creditDays} days left on your old plan`
            : `12 months. ${formatINR(membership.total)} with GST`
        : "No joining fee";

    return (
        <div className="relative overflow-hidden rounded-2xl bg-[#0B67FC] p-4 text-white shadow-md">
            <Crown className="pointer-events-none absolute -right-4 -top-4 h-28 w-28 text-white/15" aria-hidden />
            <div className="relative flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/30">
                    <Crown size={20} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white/80">Membership</p>
                    <p className="truncate text-base font-bold">{membership.name}</p>
                </div>
                <span className="shrink-0 rounded-full bg-white px-3 py-1 text-sm font-bold text-[#0B67FC] shadow-sm">{status}</span>
            </div>
            <p className="relative mt-3 text-xs text-white/85">{note}</p>
        </div>
    );
}

function PaymentSkeletonState({ title, message, actionLabel, onAction }) {
    return (
        <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                <CircleX className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{message}</p>
            <button onClick={onAction} className="mt-6 rounded-lg bg-[#0B67FC] px-5 py-2 text-sm font-semibold text-white shadow">
                {actionLabel}
            </button>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────
 * Page
 * ──────────────────────────────────────────────────────────────────────── */

export default function StorePayment() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user } = useGoerOneAuth();
    const apiKey = getApiKey();

    const domainFull = 'https://goertrip.club'
    const custUser = user;
    const customerId = user?.id;

    // Field names below are best guesses for the profile object: adjust to your user payload
    const customer = {
        name:
            [custUser?.fname ?? custUser?.first_name, custUser?.lname ?? custUser?.last_name].filter(Boolean).join(" ") ||
            custUser?.name ||
            "",
        mobile: custUser?.mobile ?? custUser?.phone ?? "",
        email: custUser?.email ?? "",
    };

    const order = useMemo(() => loadCheckoutOrder(state?.order), [state]);
    const valid = isValidOrder(order);
    // true only when this order buys/renews/upgrades a membership (an already-owned one is not a purchase)
    const membershipPurchase = !!(order?.membership && !order.membership.owned);
    const summary = useMemo(() => (valid ? buildOrderSummary(order) : null), [order, valid]);
    const grand = summary?.total ?? 0;
    const dispatch = useDispatch();

    const {
        balance: walletData,
        balanceLoading: walletLoading,
    } = useSelector((state) => state.wallet);

    const [bookableBalance, setBookableBalance] = useState(0);
    const [totalRewardEarn, setTotalRewardEarn] = useState(0);

    const [paymentMode, setPaymentMode] = useState("2"); // "1" wallet, "2" online
    const [selectedMethod, setSelectedMethod] = useState("upi");
    const [usewallet, setUsewallet] = useState(false); // partial wallet on top of an online payment
    const [useGoerCoin, setUseGoerCoin] = useState(false);
    const [accepted, setAccepted] = useState(true);

    const [openConfirm, setOpenConfirm] = useState(false);
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const [status, setStatus] = useState("loading"); // loading | success | paymentfailed | failed
    const [errorMessage, setErrorMessage] = useState("");
    const referenceRef = useRef(null); // reference number of the current attempt (sent encrypted + shown on confirmation page)

    // rdm 0 if only membership purchase else 1
    const onlyMembershipPurchase = membershipPurchase && !order?.paybill && !order?.vouchers?.length;

    // set when the order came from POST /voucher/voucher-checkout
    const isVoucherOrder = !!order?.voucherOrder;
    const voucherTxnId = order?.voucherPayment?.merchant_txn_id ?? null;

    // Vouchers can only be used when paying a bill (not membership-only, not buying vouchers)
    const showVouchers = !!summary?.bill && !!order?.store?.id && !isVoucherOrder;
    const appliedVoucher = useSelector((s) => s.storeVoucher.applied);
    const voucherCode = showVouchers ? appliedVoucher?.code ?? null : null;
    // voucher value comes off the payable amount (never below zero)
    const voucherValue = voucherCode ? Math.min(Number(appliedVoucher?.value) || 0, grand) : 0;
    const payableBase = round2(grand - voucherValue);

    // start every checkout without a leftover voucher
    useEffect(() => {
        dispatch(clearApplied());
    }, [dispatch]);

    /* ── wallet balance ── */
    useEffect(() => {
    if (!customerId) {
        setBookableBalance(0);
        setTotalRewardEarn(0);
        return;
    }

    dispatch(
        fetchWalletBalance({
            customer_id: customerId,
        })
    );
    }, [customerId, dispatch]);

    useEffect(() => {
        if (!customerId || !walletData?.status?.success) return;
        const balances = walletData.balances || {};
        const bookable = Number(balances.bookable_balance) || 0;
        setBookableBalance(bookable);
        setTotalRewardEarn(Number(balances.reward_balance?.total_rewardearn) || 0);
        // wallet covers it in full -> pre-select wallet, otherwise online
        // setPaymentMode(bookable >= grand && grand > 0 ? "1" : "2");
        setPaymentMode(bookable >= payableBase && payableBase > 0 ? "1" : "2");
        setSelectedMethod("upi");
    }, [walletData, payableBase, customerId]);

    /* ── amounts ── */
    // GoerCoin = reward_balance.total_rewardearn (1 coin = ₹1). Optional: if the customer has any, they can use it to reduce the amount.
    const goerCoin = totalRewardEarn;
    const { percent: maxCoinPercent, usable: usableCoins } = getCoinLimit(order?.membership, payableBase, goerCoin);
    const rewardApplied = useGoerCoin ? usableCoins : 0;
    const afterCoins = round2(payableBase - rewardApplied);

    // true only when the membership limit (not the order size) leaves some of the user's coins unusable
    const coinsLimitedByMembership = maxCoinPercent < 100 && goerCoin > usableCoins;

    // If GoerCoins cover the full bill, force the wallet/direct redemption flow.
    // A zero balance due after rewards must not make the Wallet option unavailable.
    const rewardsCoverOrder = useGoerCoin && payableBase > 0 && afterCoins <= 0;
    const walletCovers = afterCoins <= 0 || bookableBalance >= afterCoins;
    const walletMode = rewardsCoverOrder || (paymentMode === "1" && walletCovers);
    const partialWallet = !rewardsCoverOrder && paymentMode === "2" && usewallet && bookableBalance > 0 && afterCoins > 0;
    const walletApplied = walletMode ? afterCoins : partialWallet ? round2(Math.min(bookableBalance, afterCoins)) : 0;
    const totalPayable = walletMode ? afterCoins : round2(afterCoins - walletApplied);
    const gatewayAmount = walletMode ? 0 : totalPayable;
    const directPay = walletMode || gatewayAmount <= 0; // no gateway needed
    const onlineMethodsDisabled = rewardsCoverOrder;

    // Select Wallet when GoerCoins cover the full bill. If that stops being true,
    // return to online payment when the wallet cannot cover the remaining amount.
    useEffect(() => {
        if (rewardsCoverOrder) {
            setPaymentMode("1");
            setUsewallet(false);
            return;
        }
        if (paymentMode === "1" && !walletCovers) setPaymentMode("2");
    }, [paymentMode, walletCovers, rewardsCoverOrder]);

    /* ── placing the order ── */
    const finishSuccess = () => {
        toastr.success("Payment successful");
        setStatus("success");
        // capture everything the confirmation page needs BEFORE the order is cleared
        const referenceNumber = isVoucherOrder ? order.voucherOrder.order_no : referenceRef.current;
        const confirmationState = {
            referenceNumber,
            storeName: order?.store?.name,
            storeId: order?.store?.id,
            amount: totalPayable,
            title: summary?.title,
            ...(isVoucherOrder ? { purchaseType: "voucher" } : {}),
        };
        clearCheckoutOrder();
        setTimeout(
            () =>
                navigate(`/web/payment-confirmation?ref=${encodeURIComponent(referenceNumber)}`, {
                    replace: true,
                    state: confirmationState,
                }),
            2000
        );
    };

    const { initiatePayment } = usePaymentGateway({
        onSuccess: finishSuccess,
        onFailure: (response) => {
            setErrorMessage(response?.status || "Failed to process payment");
            setStatus("paymentfailed");
            toastr.error("Payment failed. Please contact the support team.");
        },
    });

    const buildPayload = () =>
        buildOrderPayload(order, summary, customerId, {
            usewallet: walletMode || partialWallet,
            usereward: useGoerCoin,
            walletApplied,
            rewardApplied,
            gatewayAmount,
        });
    // Body for POST /loyalty/redeem (wallet) and the encrypted udf7 (online gateway)
    const buildRedeemPayload = ({ withVoucher = false } = {}) => {
        // Vouchers: only the gateway txn id and the GoerCoin flag
        if (isVoucherOrder) {
            return { merchant_txn_id: voucherTxnId, usereward: useGoerCoin ? 1 : 0, atomTxnId: order.voucherOrder.order_no };
        }
        return {
        // uw: usewallet ? 1 : 0,  //use wallet
        // paymentMode: 1,
        ur:useGoerCoin ? 1 : 0,  //use reward
        // goercoin_required: coinsRequired, // taken from the balance, not taken off the amount
        // goercoin_discount: rewardApplied, // spare coins used to reduce the amount
        // goercoin_used: coinsUsed, // goercoin_required + goercoin_discount
        order_amount: Math.ceil(Number(summary?.bill?.amount)) ?? 0,
        // A membership the user already owns (active, not expired) needs nothing
        // charged/created server-side for it, so send null. Buying/renewing/upgrading
        // sends the program id so the backend knows which membership to apply.
        membershipId: membershipPurchase ? order.membership.membershipId : null,
        mep: onlyMembershipPurchase ? 1:0,
        reward_id: order?.paybill?.rewardId ?? null,
        store_id: order?.store?.id ?? null,
        ref: referenceRef.current,
        // phone: Number(order?.phone || customer.mobile) || "",
        rdm: onlyMembershipPurchase ? 0 : 1,  //rdm 0 if only membership purchase else 1
        ...(withVoucher && voucherCode ? { voucher_code: voucherCode } : {}),
        }
    };

    // Wallet / fully-covered voucher orders -> POST /voucher/voucher-purchase
    const submitVoucherPurchase = async () => {
        const res = await purchaseVoucherOrder({
            amount: order.voucherPayment?.amount,     // amount paid from the wallet
            statusCode: VOUCHER_META.statusCode,
            usereward: useGoerCoin ? 1 : 0,
            atomTxnId: order.voucherOrder.order_no,    // reference number
            merchant_txn_id: voucherTxnId,
        });
        return { ok: isApiSuccess(res), message: res?.message };
    };

    const buildOnlineFormData = (payload) => {
        const txnid = "TXN" + Date.now();
        const productinfo = "Store Purchase";
        const method = ONLINE_METHODS.find((m) => m.id === selectedMethod);
        const hash = WalletUtils.walletCode(txnid, txnid, gatewayAmount.toFixed(2), customer.name, customer.mobile, customer.email, productinfo);
        
        const encrypt = encryptAES(JSON.stringify(buildRedeemPayload()), apiKey, apiKey);
        
        const returnUrl = `${domainFull}/payment/payment-success?planType=${isVoucherOrder ? VOUCHER_META.planType : PURCHASE_META.planType}&BookingID=${txnid}`;
        return {
            name: customer.name,
            email: customer.email,
            phone: customer.mobile,
            productinfo,
            surl: returnUrl,
            furl: returnUrl,
            udf1: txnid,
            udf2: isVoucherOrder ? VOUCHER_META.udf2 : 1,
            udf3: PURCHASE_META.channel,
            udf4: customerId,
            udf5: custUser?.AgencyID,
            udf6: "INR",
            udf8: voucherCode ?? "",
            udf9: "",
            udf10: "",
            show_payment_mode: method?.pg,
            txnid,
            amount: gatewayAmount.toFixed(2),
            udf7: encrypt,
            securecode: hash,
        };
    };

    const handleProceed = async () => {
        if (!accepted) {
            toastr.error("Please accept the Terms & Conditions to continue.");
            return;
        }
        if (paymentMode === "1" && !walletCovers) {
            toastr.error("Insufficient wallet balance. Please choose an online method.");
            return;
        }
        if (isVoucherOrder && !voucherTxnId) {
            toastr.error("Voucher order is missing a payment reference. Please go back and try again.");
            return;
        }
        setOpenConfirm(false);
        setIsStatusOpen(true);
        setStatus("loading");

        // Orders that came from a store bill QR reuse the ref issued by /store-pay/create, so the backend can match this payment to the bill
        referenceRef.current = order?.storeBill?.ref || generateReferenceNumber();
        const payload = buildPayload();
        // console.log(buildRedeemPayload())
        if (!directPay) {
            initiatePayment(buildOnlineFormData(payload));
            return;
        }
        try {
           
            // const res = await submitOrderDirect(buildRedeemPayload());
            const res = isVoucherOrder
            ? await submitVoucherPurchase()
            : await submitOrderDirect(buildRedeemPayload({ withVoucher: true }));

            if (res?.ok) finishSuccess();
            else {
                setErrorMessage(res?.message || "Your order could not be completed.");
                setStatus("failed");
            }
        } catch (err) {
            const msg = err?.data?.message || err?.message || "Your order could not be completed.";
            setErrorMessage(msg);
            setStatus("failed");
            toastr.error(msg);
        }
    };

    /* ── guards (after all hooks) ── */
    if (!customerId) {
        return (
            <PaymentSkeletonState
                title="Log in to continue"
                message="You need to be logged in to pay at a store."
                actionLabel="Log in"
                onAction={() => navigate(LOGIN_PATH)}
            />
        );
    }
    if (!valid) {
        return (
            <PaymentSkeletonState
                title="Nothing to pay for"
                message="This checkout has expired. Go back to the store and start again."
                actionLabel="Go back"
                onAction={() => navigate(-1)}
            />
        );
    }

    const { bill, vouchers, membership } = summary;
    const address = order.store ? [order.store.address, order.store.cityName].filter(Boolean).join(", ") : "";
    const showMethods = grand > 0;

    const payLabel = totalPayable > 0 ? `Pay ${formatINR(totalPayable)}` : "Confirm order";

    return (
        <div className="min-h-screen bg-slate-50 pb-28 lg:pb-10">
            <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                <div className="mb-5 flex items-start gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="mt-0.5 rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-100"
                        aria-label="Back"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Complete your payment</h1>
                        <p className="truncate text-sm text-slate-500">{summary.title}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* ── Left column ─────────────────────────────── */}
                    <div className="space-y-6 lg:col-span-2">
                        {/* Paying to + what is being bought */}
                        {order.store ? (
                            <Card>
                                <div className="flex items-center gap-4 p-5 sm:p-6">
                                    {order.store.logo ? (
                                        <img
                                            src={order.store.logo}
                                            alt={order.store.name}
                                            className="h-16 w-16 shrink-0 rounded-xl bg-slate-100 object-cover"
                                            onError={(e) => { e.currentTarget.src = "/store-placeholder.png"; }}
                                        />
                                    ) : (
                                        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#0B67FC]/5 text-[#0B67FC]">
                                            <StoreIcon size={26} />
                                        </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-medium text-slate-500">Paying to</p>
                                        <h2 className="truncate text-lg font-semibold text-slate-900">{order.store.name}</h2>
                                        {address && (
                                            <p className="mt-0.5 flex items-start gap-1 text-sm text-slate-500">
                                                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                <span className="line-clamp-2">{address}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <ul className="divide-y divide-slate-100 border-t border-slate-100 px-5 sm:px-6">
                                    {bill && (
                                        <li className="flex items-center gap-3 py-3">
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0B67FC]/5 text-[#0B67FC]">
                                                <Receipt size={17} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-slate-900">Bill payment</p>
                                                <p className="truncate text-xs text-slate-500">
                                                    {bill.rewardName ? `Offer applied: ${bill.rewardName}` : "No offer applied"}
                                                </p>
                                            </div>
                                        </li>
                                    )}
                                    {vouchers?.items.map((v) => (
                                        <li key={v.voucher_id} className="flex items-center gap-3 py-3">
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0B67FC]/5 text-[#0B67FC]">
                                                <Ticket size={17} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-slate-900">{v.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    Voucher, quantity {v.qty}{vouchers.orderNo ? ` · Order ${vouchers.orderNo}` : ""}
                                                </p>
                                            </div>
                                            <span className="shrink-0 text-sm font-medium text-slate-900">{formatINR(v.linePayable)}</span>
                                        </li>
                                    ))}
                                    {!bill && !vouchers && membership && (
                                        <li className="py-3 text-sm text-slate-600">Membership purchase only</li>
                                    )}
                                </ul>
                            </Card>
                        ) : (
                            membership && (
                                <Card>
                                    <div className="flex items-center gap-4 p-5 sm:p-6">
                                        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#0B67FC]/5 text-[#0B67FC]">
                                            <Crown size={26} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-slate-500">Paying for</p>
                                            <h2 className="truncate text-lg font-semibold text-slate-900">{membership.name} membership</h2>
                                        </div>
                                    </div>
                                </Card>
                            )
                        )}

                        {membership && <MembershipCard membership={membership} credit={order?.membership?.credit || 0} creditDays={order?.membership?.creditDays || 0} />}
                        {showVouchers && <StoreVoucherPicker storeId={order.store.id} billAmount={Number(bill.amount) || 0} />}
                        {/* Paid by */}
                        <Card title="Paid by">
                            <div className="flex items-center gap-3 px-5 py-4 sm:px-6">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                    <User size={18} />
                                </span>
                                <div className="min-w-0">
                                    <p className="truncate font-semibold text-slate-900">{customer.name || "Your account"}</p>
                                    <p className="truncate text-sm text-slate-500">
                                        {[customer.mobile, customer.email].filter(Boolean).join(" · ")}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        {/* Payment method */}
                        <Card className="overflow-hidden pb-4 sm:pb-0">
                            <div className="p-5 sm:p-6">
                                {showMethods ? (
                                    <>
                                        <h4 className="mb-3 font-semibold text-slate-800">Choose payment method</h4>
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                            {/* Wallet */}
                                            <label
                                                htmlFor="wallet"
                                                className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-3 transition ${
                                                    paymentMode === "1" ? "border-[#0B67FC] bg-[#0B67FC]/5" : "border-slate-200"
                                                } ${!walletCovers ? "cursor-not-allowed opacity-50" : ""}`}
                                            >
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                                                    <img src={walletIcon} alt="Wallet" className="h-5 w-5 object-contain" />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-medium text-slate-800">Wallet</span>
                                                    <span className="block truncate text-xs text-slate-500">{formatINR(bookableBalance)}</span>
                                                </span>
                                                <input
                                                    type="radio"
                                                    id="wallet"
                                                    name="paymentMode"
                                                    value="1"
                                                    checked={paymentMode === "1"}
                                                    disabled={!walletCovers}
                                                    onChange={() => setPaymentMode("1")}
                                                    className="h-4 w-4 shrink-0 accent-[#0B67FC]"
                                                />
                                            </label>

                                            {ONLINE_METHODS.map((m) => {
                                                const selected = paymentMode === "2" && selectedMethod === m.id;
                                                return (
                                                    <label
                                                        key={m.id}
                                                        htmlFor={`method-${m.id}`}
                                                        className={`relative flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-3 transition ${
                                                            selected ? "border-[#0B67FC] bg-[#0B67FC]/5" : "border-slate-200"
                                                        } ${m.recommended ? "border-2" : ""} ${onlineMethodsDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                                                    >
                                                        {m.recommended && (
                                                            <span className="absolute -top-2.5 right-2 whitespace-nowrap rounded-full bg-emerald-700 px-1.5 py-0.5 text-[9px] font-semibold text-white">
                                                                Recommended
                                                            </span>
                                                        )}
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-base">
                                                            {m.icon ? <img src={m.icon} alt={m.label} className="h-5 w-5 object-contain" /> : m.emoji}
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block truncate text-sm font-medium text-slate-800">{m.label}</span>
                                                            {m.sub && <span className="block truncate text-xs text-slate-500">{m.sub}</span>}
                                                            {m.logos && (
                                                                <span className="mt-1 flex flex-wrap items-center gap-2">
                                                                    {m.logos.map(([src, alt, cls]) => (
                                                                        <img key={alt} src={src} alt={alt} className={`${cls} w-auto object-contain`} />
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <input
                                                            type="radio"
                                                            id={`method-${m.id}`}
                                                            name="paymentMode"
                                                            checked={selected}
                                                            disabled={onlineMethodsDisabled}
                                                            onChange={() => {
                                                                if (onlineMethodsDisabled) return;
                                                                setSelectedMethod(m.id);
                                                                setPaymentMode("2");
                                                            }}
                                                            className="h-4 w-4 shrink-0 accent-[#0B67FC]"
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>

                                        <div className="mt-4 space-y-2">
                                            {!rewardsCoverOrder && paymentMode === "2" && bookableBalance > 0 && bookableBalance < afterCoins && (
                                                <label htmlFor="usewallet" className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#0B67FC]/20 bg-[#0B67FC]/5 px-4 py-2.5 text-sm text-[#062E6C]">
                                                    <input
                                                        type="checkbox"
                                                        id="usewallet"
                                                        checked={usewallet}
                                                        onChange={(e) => setUsewallet(e.target.checked)}
                                                        className="h-4 w-4 rounded accent-[#0B67FC]"
                                                    />
                                                    Use wallet balance ({formatINR(bookableBalance)})
                                                </label>
                                            )}
                                            {/* {goerCoin > 0 && (
                                                <label htmlFor="usegoercoin" className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#0B67FC]/20 bg-[#0B67FC]/5 px-4 py-2.5 text-sm text-[#062E6C]">
                                                    <input
                                                        type="checkbox"
                                                        id="usegoercoin"
                                                        checked={useGoerCoin}
                                                        onChange={(e) => setUseGoerCoin(e.target.checked)}
                                                        className="h-4 w-4 rounded accent-[#0B67FC]"
                                                    />
                                                    Use GoerCoin ({goerCoin})
                                                </label>
                                            )} */}
                                           {goerCoin > 0 && maxCoinPercent === 0 && (
                                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                                                <p className="flex items-center gap-2 font-medium">
                                                    <Crown size={16} className="shrink-0" />
                                                    Upgrade your membership to use GoerCoin
                                                </p>
                                                <p className="mt-1 text-xs">Your GoerCoin balance: {goerCoin}</p>
                                            </div>
                                        )}
                                        {usableCoins > 0 && (
                                            <div>
                                                <label htmlFor="usegoercoin" className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#0B67FC]/20 bg-[#0B67FC]/5 px-4 py-2.5 text-sm text-[#062E6C]">
                                                    <input
                                                        type="checkbox"
                                                        id="usegoercoin"
                                                        checked={useGoerCoin}
                                                        onChange={(e) => setUseGoerCoin(e.target.checked)}
                                                        className="h-4 w-4 rounded accent-[#0B67FC]"
                                                    />
                                                    Use GoerCoin ({usableCoins})
                                                </label>
                                                {coinsLimitedByMembership && (
                                                    <div className="mt-1 space-y-0.5 px-1 text-xs">
                                                        <p className="text-slate-500">Your GoerCoin balance: {goerCoin}</p>
                                                        <p className="flex items-center gap-1 text-amber-700">
                                                            <Crown size={12} className="shrink-0" />
                                                            Your membership allows up to {maxCoinPercent}% of this order in coins. Upgrade your membership to use 100% of coins.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-slate-600">Nothing to pay on this order. Confirm to complete it.</p>
                                )}

                                <label htmlFor="accepttncTerms" className="mt-5 flex cursor-pointer items-start gap-2 text-sm text-slate-600">
                                    <input
                                        id="accepttncTerms"
                                        type="checkbox"
                                        checked={accepted}
                                        onChange={(e) => setAccepted(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[#0B67FC]"
                                    />
                                    <span>
                                        I agree to the{" "}
                                        <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#0954D0] underline">Privacy Policy</a>,{" "}
                                        <a href="/terms-conditions" target="_blank" rel="noreferrer" className="text-[#0954D0] underline">Terms &amp; Conditions</a> and{" "}
                                        <a href="/terms-conditions" target="_blank" rel="noreferrer" className="text-[#0954D0] underline">User Agreement</a>.
                                    </span>
                                </label>

                                <div className="hidden items-center justify-between pt-6 lg:flex">
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="cursor-pointer rounded-lg border border-slate-200 px-5 py-2 text-slate-700 hover:bg-slate-50"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOpenConfirm(true)}
                                        disabled={!accepted}
                                        className="flex cursor-pointer items-center gap-2 rounded-lg bg-[#0B67FC] px-6 py-2.5 font-semibold text-white shadow transition hover:bg-[#0954D0] disabled:bg-slate-300"
                                    >
                                        <Lock className="h-4 w-4" /> {payLabel}
                                    </button>
                                </div>
                            </div>

                            {/* Sticky pay bar (mobile) */}
                            <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] lg:hidden">
                                <div>
                                    <p className="text-xs text-slate-500">Payable</p>
                                    <p className="text-lg font-bold text-slate-900">{formatINR(totalPayable)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setOpenConfirm(true)}
                                    disabled={!accepted}
                                    className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#0B67FC] px-5 py-2.5 text-sm font-semibold text-white shadow disabled:bg-slate-300"
                                >
                                    <Lock className="h-4 w-4" /> {totalPayable > 0 ? "Pay now" : "Confirm"}
                                </button>
                            </div>
                        </Card>
                    </div>

                    {/* ── Right column: bill summary ──────────────── */}
                    <aside className="lg:sticky lg:top-6 lg:self-start">
                        <Card title="Bill summary">
                            <div className="px-5 py-5 sm:px-6">
                                <ul className="divide-y divide-slate-100">
                                    {bill && (
                                        <>
                                            <SummaryRow label="Bill amount" value={bill.amount} />
                                            {bill.discount > 0 && (
                                                <SummaryRow label="Offer discount" sub={bill.rewardName} value={bill.discount} negative />
                                            )}
                                        </>
                                    )}
                                    {vouchers && (
                                        <>
                                            <SummaryRow label={`Vouchers (${vouchers?.count})`} value={vouchers?.face} />
                                            {vouchers?.discount > 0 && <SummaryRow label="Voucher discount" value={vouchers?.discount} negative />}
                                        </>
                                    )}
                                    {vouchers?.tax > 0 && <SummaryRow label="Tax" value={vouchers?.tax} />}
                                    {membership && !membership.owned && (
                                        <>
                                            {order?.membership?.credit > 0 && (
                                                <>
                                                    <SummaryRow label="Membership price" sub={membership?.name} value={order.membership.fullFee} />
                                                    <SummaryRow label={`Credit for ${order.membership.creditDays} days left`} value={order.membership.credit} negative />
                                                </>
                                            )}
                                            <SummaryRow
                                                label="Membership fee"
                                                sub={membership?.name}
                                                {...(membership?.fee > 0
                                                    ? { value: membership?.fee }
                                                    : { text: membership?.owned ? "Active" : "Free" })}
                                            />
                                            {membership?.fee > 0 && <SummaryRow label="GST on membership (18%)" value={membership.gst} />}
                                        </>
                                    )}
                                    {voucherValue > 0 && <SummaryRow label="Voucher applied" sub={appliedVoucher?.code} value={voucherValue} negative />}
                                    {rewardApplied > 0 && <SummaryRow label="GoerCoin applied" value={rewardApplied} negative />}
                                    {partialWallet && walletApplied > 0 && <SummaryRow label="Wallet applied" value={walletApplied} negative />}
                                </ul>

                                <div className="mt-4 flex items-center justify-between rounded-xl bg-[#0B67FC]/5 px-4 py-3">
                                    <span className="text-sm font-semibold text-[#062E6C]">Total payable</span>
                                    <span className="text-lg font-bold text-[#062E6C]">{formatINR(totalPayable)}</span>
                                </div>
                                {walletMode && (
                                    <p className="mt-2 text-xs text-slate-500">This amount will be deducted from your wallet.</p>
                                )}
                                

                                {summary.savings > 0 && (
                                    <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                                        <Sparkles size={14} /> You're saving {formatINR(summary.savings)} on this order
                                    </p>
                                )}
                                {summary.cashback > 0 && (
                                    <p className="mt-2 flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                                        <Wallet size={14} className="shrink-0" /> You'll earn {formatINR(summary.cashback)} cashback
                                    </p>
                                )}
                                <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                    <ShieldCheck className="h-4 w-4" /> 100% secure payments
                                </p>
                            </div>
                        </Card>
                    </aside>
                </div>
            </div>

            {/* Confirm dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
                <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-lg">
                    <Dialog.Title className="text-lg font-semibold text-slate-900">Confirm before proceeding</Dialog.Title>
                    <p className="mb-6 mt-2 text-sm text-slate-600">
                        {totalPayable > 0
                            ? `You are about to pay ${formatINR(totalPayable)}${order.store ? ` to ${order.store.name}` : ""}.`
                            : `You are about to complete your order${order.store ? ` at ${order.store.name}` : ""}.`}
                    </p>
                    <div className="flex justify-center gap-3">
                        <button type="button" onClick={() => setOpenConfirm(false)} className="cursor-pointer rounded-lg border border-slate-200 px-4 py-2 text-slate-700">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleProceed}
                            className="cursor-pointer rounded-lg bg-[#0B67FC] px-5 py-2 font-semibold text-white shadow"
                        >
                            {totalPayable > 0 ? "Proceed to pay" : "Confirm order"}
                        </button>
                    </div>
                </Dialog.Panel>
            </Dialog>

            {/* Status dialog */}
            <Dialog open={isStatusOpen} onClose={() => {}} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
                <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-lg">
                    {status === "loading" && (
                        <>
                            <Dialog.Title className="w-full border-b border-slate-100 pb-2 text-lg font-semibold text-slate-900">Processing your payment…</Dialog.Title>
                            <div className="mt-6 flex flex-col items-center">
                                <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#0B67FC] border-t-transparent" />
                                <p className="mt-4 text-sm text-slate-500">
                                    Please don't go back or refresh.
                                    <br />
                                    This might take a few seconds.
                                </p>
                            </div>
                        </>
                    )}
                    {status === "success" && (
                        <>
                            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
                            <h2 className="text-xl font-bold text-emerald-600">Payment successful</h2>
                            <p className="mt-2 text-sm text-slate-600">{summary.title}</p>
                        </>
                    )}
                    {(status === "paymentfailed" || status === "failed") && (
                        <>
                            <CircleX className="mx-auto mb-4 h-16 w-16 text-rose-500" />
                            <h2 className="text-xl font-bold text-rose-600">{status === "paymentfailed" ? "Payment failed" : "Order failed"}</h2>
                            <p className="mt-2 text-sm text-slate-600">{errorMessage}</p>
                            <button
                                onClick={() => setIsStatusOpen(false)}
                                className="mt-6 cursor-pointer rounded-lg bg-[#0B67FC] px-6 py-2 font-semibold text-white shadow"
                            >
                                Try again
                            </button>
                        </>
                    )}
                </Dialog.Panel>
            </Dialog>
        </div>
    );
}